import type { useQueryClient } from "@tanstack/react-query";
import type { EmailAccountListItem } from "@/lib/email-accounts";
import type { DwdConnectedEmailsParams, DwdSyncPreviewParams } from "./api";
import type { DwdConnectedEmails, DwdConnectedWarmup } from "./schemas";

/**
 * The DWD query keys plus the one cross-feature cache writer that reads them.
 * Separate from `./queries` so `email-accounts/queries` can import the writer
 * without a cycle — so this module must import neither feature's `queries.ts`.
 */

/**
 * Hierarchical query-key factory for the DWD bulk-connect-workspace domain.
 * Mirrors `emailAccountKeys`: `all` is the bare scope (root for blanket
 * invalidation), intermediate scopes group by kind, and leaf functions take the
 * variant keys. None of the keys carries `workspaceId` at the top level beyond
 * the resource it identifies — the JWT carries the tenant, and the
 * workspace-switch unfiltered invalidate refetches everything.
 */
export const dwdWorkspaceKeys = {
  all: ["dwd-workspace"] as const,
  workspaces: () => [...dwdWorkspaceKeys.all, "workspaces"] as const,
  previews: () => [...dwdWorkspaceKeys.all, "sync-preview"] as const,
  preview: (workspaceId: number, params: DwdSyncPreviewParams) =>
    [...dwdWorkspaceKeys.previews(), workspaceId, params] as const,
  syncStatus: (workspaceId: number, jobId: string) =>
    [...dwdWorkspaceKeys.all, "sync-status", workspaceId, jobId] as const,
  connectedEmailsAll: () =>
    [...dwdWorkspaceKeys.all, "connected-emails"] as const,
  connectedEmails: (workspaceId: number, params: DwdConnectedEmailsParams) =>
    [...dwdWorkspaceKeys.connectedEmailsAll(), workspaceId, params] as const,
};

type DwdConnectedEmailRow = DwdConnectedEmails["items"][number];

/** Merges `patch` into every cached connected-emails row for `emailAccountId`. */
function patchDwdConnectedEmailRow(
  qc: ReturnType<typeof useQueryClient>,
  emailAccountId: number,
  patch: Partial<DwdConnectedEmailRow>,
): void {
  qc.setQueriesData<DwdConnectedEmails>(
    { queryKey: dwdWorkspaceKeys.connectedEmailsAll() },
    (prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((row) =>
              row.emailAccountId === emailAccountId ? { ...row, ...patch } : row,
            ),
          }
        : prev,
  );
}

/**
 * Mirrors an account's warmup state onto every cached connected-emails row.
 * Called from `propagateAccountUpdate`, not the page: the warmup endpoints
 * return the PRE-transition row, so only the poll's later snapshot is useful.
 */
export function patchDwdConnectedEmailWarmup(
  qc: ReturnType<typeof useQueryClient>,
  account: Pick<
    EmailAccountListItem,
    "id" | "warmupStatus" | "accountState" | "statusBucket" | "warmupPausedReason"
  >,
): void {
  const warmup: DwdConnectedWarmup = {
    warmupStatus: account.warmupStatus,
    accountState: account.accountState,
    statusBucket: account.statusBucket,
    warmupPausedReason: account.warmupPausedReason,
  };
  patchDwdConnectedEmailRow(qc, account.id, { warmup });
}

/** Mirrors a saved sender name onto the Manage-Mailboxes rows. */
export function patchDwdConnectedEmailSenderName(
  qc: ReturnType<typeof useQueryClient>,
  emailAccountId: number,
  senderName: string | null,
): void {
  patchDwdConnectedEmailRow(qc, emailAccountId, { senderName });
}
